import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Footer from '../landing-page/components/Footer';
import Button from '../../components/ui/Button';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { useAuth } from '../../contexts/AuthContext';

const AboutUs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'About Us', path: null }
  ];

  const values = [
    {
      icon: 'Sparkles',
      title: 'Simplicity',
      description: 'Clear tools without unnecessary complexity'
    },
    {
      icon: 'Eye',
      title: 'Transparency',
      description: 'No hidden practices or confusing features'
    },
    {
      icon: 'Shield',
      title: 'Privacy-First Design',
      description: 'Your data stays yours'
    },
    {
      icon: 'Users',
      title: 'User Empowerment',
      description: 'Helping users feel informed, not judged'
    },
    {
      icon: 'Lock',
      title: 'Trust & Security',
      description: 'Protecting user information at every level'
    }
  ];

  const features = [
    'Track income and expenses in one place',
    'Organize and categorize transactions',
    'Monitor spending patterns over time',
    'Stay aware of upcoming payments and obligations',
    'Gain insights that support better financial awareness'
  ];

  return (
    <div className="min-h-screen bg-background">
      {user ? (
        <>
          <Header />
          <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-8 md:py-12 mt-16">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </>
      ) : (
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-8 md:py-12">
          <button
            onClick={() => navigate('/landing-page')}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-8 font-medium">
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Home</span>
          </button>
        </div>
      )}

      <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-8 md:py-12 mt-16">
        {/* Hero Section */}
        <div className="mb-16 md:mb-20">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
            About Rovyn Budget
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground font-medium max-w-4xl">
            Take Control of Your Money — With Clarity and Confidence
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-16 md:mb-20">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-foreground/90 leading-relaxed mb-6">
              Rovyn Budget is a digital budgeting and financial tracking platform built to help everyday people better understand and manage their money. We believe financial clarity shouldn't be complicated, overwhelming, or reserved for experts — it should be accessible, practical, and empowering.
            </p>
            <p className="text-lg text-foreground/90 leading-relaxed">
              Our goal is simple: to give you a clear view of your finances so you can make informed decisions with confidence.
            </p>
          </div>
        </div>

        {/* Mission Section */}
        <div className="mb-16 md:mb-20">
          <div className="bg-card rounded-2xl p-8 md:p-12 border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/20 rounded-lg">
                <Icon name="Target" size={24} color="var(--color-primary)" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold">Our Mission</h2>
            </div>
            <p className="text-lg text-foreground/90 leading-relaxed mb-4">
              Our mission is to simplify money management and reduce financial stress by giving users the tools they need to see, organize, and plan their finances more effectively. Rovyn Budget is designed to support healthier financial habits through visibility, structure, and thoughtful insights.
            </p>
            <p className="text-xl font-semibold text-primary">
              We focus on clarity — not complexity.
            </p>
          </div>
        </div>

        {/* What Rovyn Budget Does */}
        <div className="mb-16 md:mb-20">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">What Rovyn Budget Does</h2>
          <p className="text-lg text-foreground/90 leading-relaxed mb-8">
            Rovyn Budget helps users:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {features?.map((feature, index) => (
              <div key={index} className="flex items-start gap-3 bg-card p-6 rounded-xl border border-border">
                <div className="flex-shrink-0 mt-1">
                  <Icon name="Check" size={20} color="var(--color-primary)" strokeWidth={3} />
                </div>
                <p className="text-base text-foreground/90">{feature}</p>
              </div>
            ))}
          </div>
          <p className="text-lg text-foreground/90 leading-relaxed">
            Whether you're just starting your budgeting journey or looking for a cleaner way to manage multiple accounts, Rovyn Budget is designed to work around your real-life financial needs.
          </p>
        </div>

        {/* What We Are Not */}
        <div className="mb-16 md:mb-20">
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="AlertCircle" size={28} color="var(--color-warning)" />
              <h2 className="text-3xl md:text-4xl font-heading font-bold">What We Are Not</h2>
            </div>
            <p className="text-lg text-foreground/90 leading-relaxed">
              Rovyn Budget does not provide financial, investment, tax, or legal advice. All information, tools, and educational content — including blog posts and insights — are provided for general informational and educational purposes only.
            </p>
            <p className="text-lg text-foreground/90 leading-relaxed mt-4">
              Users remain responsible for their own financial decisions and should consider their personal circumstances or consult qualified professionals when needed.
            </p>
          </div>
        </div>

        {/* Why Created */}
        <div className="mb-16 md:mb-20">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">Why Rovyn Budget Was Created</h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-foreground/90 leading-relaxed mb-6">
              Many people struggle with scattered financial information, unclear spending, and tools that feel more stressful than helpful. Traditional budgeting solutions often rely on rigid systems or complicated spreadsheets that don't reflect how people actually manage money.
            </p>
            <p className="text-lg text-foreground/90 leading-relaxed">
              Rovyn Budget was created to be different — simple, flexible, and user-friendly — without sacrificing insight or control.
            </p>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16 md:mb-20">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">Our Values</h2>
          <p className="text-lg text-foreground/90 leading-relaxed mb-8">
            We are guided by a few core principles:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values?.map((value, index) => (
              <div key={index} className="bg-card rounded-xl p-6 border border-border hover:border-primary transition-colors">
                <div className="flex items-center justify-center w-14 h-14 bg-primary/20 rounded-lg mb-4">
                  <Icon name={value?.icon} size={28} color="var(--color-primary)" />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3">{value?.title}</h3>
                <p className="text-base text-foreground/80">{value?.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy & Data Protection */}
        <div className="mb-16 md:mb-20">
          <div className="bg-card rounded-2xl p-8 md:p-12 border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/20 rounded-lg">
                <Icon name="ShieldCheck" size={24} color="var(--color-primary)" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold">Privacy & Data Protection</h2>
            </div>
            <p className="text-lg text-foreground/90 leading-relaxed">
              We take data protection seriously. Rovyn Budget is designed with privacy and security at its core. User data is protected, encrypted, and never sold to third parties. Users remain in control of their information at all times.
            </p>
          </div>
        </div>

        {/* Looking Ahead */}
        <div className="mb-16 md:mb-20">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 border border-primary/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/20 rounded-lg">
                <Icon name="Rocket" size={24} color="var(--color-primary)" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold">Looking Ahead</h2>
            </div>
            <p className="text-lg text-foreground/90 leading-relaxed mb-6">
              Rovyn Budget is continuously evolving. We are committed to improving the platform, expanding features, and launching a mobile app to make managing your finances even more accessible — wherever life takes you.
            </p>
            <p className="text-xl font-semibold text-primary">
              Built for clarity. Designed for real life.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
            Ready to Take Control of Your Finances?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of users who have found clarity and confidence with Rovyn Budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/register')}
              className="px-8 py-3 text-lg">
              Get Started Free
            </Button>
            <Button
              onClick={() => navigate('/landing-page')}
              variant="outline"
              className="px-8 py-3 text-lg">
              Learn More
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUs;